// client/src/components/servicos/ServicoFormWrapper.js - VERSÃO 100% COMPLETA E OTIMIZADA COM SUPORTE A POOL
import React, { useEffect, useMemo, useState } from 'react';
import { useServico } from '../../hooks/useServicos';
import { useRecursos } from '../../hooks/useRecursos';
import { useRecursosPool } from '../../hooks/useRecursos';
import { useUnidades } from '../../hooks/useUnidades';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import ServicoForm from './ServicoForm';
import { Loader2, AlertCircle, Tag, Edit, PlusCircle } from 'lucide-react';

const extractId = (item) => {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (typeof item === 'object') {
        if (item.$oid) return String(item.$oid);
        if (item._id) return String(item._id);
        if (item.id) return String(item.id);
    }
    return String(item);
};

const processarServicoParaFormulario = (servico, categorias) => {
    if (!servico) return null;
    
    const servicoId = extractId(servico) || servico.servicoId;

    let categoriaProcessada = '';
    
    if (servico.categoria) {
        if (typeof servico.categoria === 'object' && servico.categoria._id) {
            categoriaProcessada = servico.categoria._id;
        }
        else if (typeof servico.categoria === 'string' && servico.categoria.trim() !== '') {
            categoriaProcessada = servico.categoria;
        }
        else if (typeof servico.categoria === 'object' && servico.categoria.id) {
            categoriaProcessada = servico.categoria.id;
        }
        
        if (categoriaProcessada) {
            const categoriaExiste = categorias.some(cat => {
                const catId = cat._id || cat.id;
                return catId === categoriaProcessada || catId?.toString() === categoriaProcessada?.toString();
            });
            
            if (!categoriaExiste) {
                const categoriaPorNome = categorias.find(cat => 
                    cat.nome === servico.categoria?.nome || 
                    (typeof servico.categoria === 'object' && servico.categoria.nome === cat.nome)
                );
                
                if (categoriaPorNome) {
                    categoriaProcessada = categoriaPorNome._id || categoriaPorNome.id;
                }
            }
        }
    }

    const servicoProcessado = {
        _id: servicoId,
        id: servicoId,
        servicoId: servicoId,
        ...servico,
        categoria: categoriaProcessada,
        
        unidadesDetalhes: Array.isArray(servico.unidadesDetalhes) ? servico.unidadesDetalhes : [],
        recursosVinculados: Array.isArray(servico.recursosVinculados) 
            ? servico.recursosVinculados.map(r => extractId(r)) 
            : [],
        // ✅ NOVOS CAMPOS: Pool de Recursos
        usaPoolRecurso: servico.usaPoolRecurso || false,
        poolRecursoId: servico.poolRecursoId ? extractId(servico.poolRecursoId) : '',
        quantidadeRecursosNecessaria: servico.quantidadeRecursosNecessaria || 1,
        recursosObrigatorios: Array.isArray(servico.recursosObrigatorios) 
            ? servico.recursosObrigatorios.map(r => extractId(r)) 
            : [],
        
        pacotesDePreco: Array.isArray(servico.pacotesDePreco) 
            ? servico.pacotesDePreco.map(pacote => ({
                ...pacote,
                sessoes: Number(pacote.sessoes) || 1,
                valor: Number(pacote.valor) || 0,
                descontoPercentual: Number(pacote.descontoPercentual) || 0,
                validadeMeses: Number(pacote.validadeMeses) || 12
            })) 
            : [],
        
        nome: servico.nome || '',
        descricao: servico.descricao || '',
        ativo: servico.ativo !== undefined ? servico.ativo : true,
        agendavel: servico.agendavel !== undefined ? servico.agendavel : true,
        duracaoSessaoEmMinutos: servico.duracaoSessaoEmMinutos || 60,
        tipoDePrecificacao: servico.tipoDePrecificacao || 'Fixo',
        precoVisivelParaCliente: servico.precoVisivelParaCliente !== undefined ? servico.precoVisivelParaCliente : true,
        
        metadata: {
            tipoServico: servico.metadata?.tipoServico || 'interno',
            disponivelOnline: servico.metadata?.disponivelOnline || false,
            requerAnamnese: servico.metadata?.requerAnamnese || false,
            tempoPreparacao: servico.metadata?.tempoPreparacao || 0,
            tempoLimpeza: servico.metadata?.tempoLimpeza || 0,
            requerDeslocamento: servico.metadata?.requerDeslocamento || false,
            raioAtendimento: servico.metadata?.raioAtendimento || 0,
            tempoDeslocamento: servico.metadata?.tempoDeslocamento || 0,
            custoDeslocamento: servico.metadata?.custoDeslocamento || 0,
            nivelComplexidade: servico.metadata?.nivelComplexidade || 'baixa',
            restricoes: Array.isArray(servico.metadata?.restricoes) ? servico.metadata.restricoes : [],
            instrucoesPreparacao: servico.metadata?.instrucoesPreparacao || '',
            observacoesInternas: servico.metadata?.observacoesInternas || '',
            ...servico.metadata
        },
        
        tags: Array.isArray(servico.tags) ? servico.tags : []
    };

    delete servicoProcessado.__v;
    delete servicoProcessado.createdAt;
    delete servicoProcessado.updatedAt;
    
    return servicoProcessado;
};

const ServicoFormWrapper = ({
    servicoId = null,
    onCloseForm,
    onCreateSuccess = () => {},
    accountId = null,
    mode = 'create',
    prefillData = null
}) => {
    const [isDataReady, setIsDataReady] = useState(false);

    const { 
        data: servico, 
        isLoading: isLoadingServico, 
        isError: isErrorServico,
        error: servicoError 
    } = useServico(servicoId, {
        enabled: !!servicoId && mode === 'edit',
    });

    const { 
        data: categoriasData, 
        isLoading: isLoadingCategorias, 
        isError: isErrorCategorias,
        error: categoriasError 
    } = useCategoriasParaFormulario(accountId, 'servico');

    const {
        data: recursosData,
        isLoading: isLoadingRecursos,
        isError: isErrorRecursos,
        error: recursosError
    } = useRecursos(accountId, { 
        limit: 100,
        ativo: true
    });

    // ✅ NOVO: Buscar pools de recursos
    const {
        data: poolsData,
        isLoading: isLoadingPools,
        isError: isErrorPools,
        error: poolsError
    } = useRecursosPool(accountId, { 
        limit: 100,
        ativo: true
    });

    const {
        data: rawUnidades,
        isLoading: isLoadingUnidades,
        isError: isErrorUnidades,
        error: unidadesError
    } = useUnidades(accountId);

    const categorias = useMemo(() => 
        Array.isArray(categoriasData) ? categoriasData : (categoriasData?.data || []), 
        [categoriasData]
    );

    const recursos = useMemo(() => 
        Array.isArray(recursosData) ? recursosData : (recursosData?.data || []), 
        [recursosData]
    );

    // ✅ NOVO: Arrays de pools
    const pools = useMemo(() => 
        Array.isArray(poolsData) ? poolsData : (poolsData?.data || []), 
        [poolsData]
    );

    const unidades = useMemo(() => 
        Array.isArray(rawUnidades) ? rawUnidades : (rawUnidades?.data || []), 
        [rawUnidades]
    );

    const servicoProcessado = useMemo(() => {
        if (mode !== 'edit' || !servico) return null;
        return processarServicoParaFormulario(servico, categorias);
    }, [servico, categorias, mode, servicoId]);

    useEffect(() => {
        if (mode === 'create') {
            if (!isLoadingCategorias && categorias.length > 0) {
                setIsDataReady(true);
            }
        } else if (mode === 'edit') {
            if (!isLoadingServico && !isLoadingCategorias && servicoProcessado && categorias.length > 0) {
                setIsDataReady(true);
            }
        }
    }, [mode, servicoId, isLoadingServico, isLoadingCategorias, servico, servicoProcessado, categorias, recursos]);

    const isLoading = isLoadingServico || isLoadingCategorias || isLoadingRecursos || isLoadingPools || isLoadingUnidades;
    const hasErrors = isErrorServico || isErrorCategorias || isErrorRecursos || isErrorPools || isErrorUnidades;

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center p-8">
                {mode === 'edit' ? (
                    <Edit size={32} className="text-blue-500 mb-4" />
                ) : (
                    <PlusCircle size={32} className="text-green-500 mb-4" />
                )}
                <Loader2 size={24} className="animate-spin text-blue-500 mb-4" />
                <p className="text-gray-600">
                    {mode === 'edit' ? 'Carregando dados do serviço...' : 'Preparando formulário...'}
                </p>
            </div>
        );
    }

    if (hasErrors) {
        return (
            <div className="alert alert-danger">
                <div className="flex items-center gap-2 mb-2">
                    <AlertCircle size={20} />
                    <h3 className="font-medium">
                        {mode === 'edit' ? 'Erro ao carregar serviço' : 'Erro ao preparar formulário'}
                    </h3>
                </div>
                <p className="text-sm mb-3">
                    {mode === 'edit' 
                        ? 'Não foi possível carregar os dados do serviço para edição.' 
                        : 'Não foi possível carregar todos os dados necessários para o formulário.'
                    }
                </p>
                <button onClick={onCloseForm} className="mt-3 action-button action-button-secondary">
                    Fechar
                </button>
            </div>
        );
    }

    if (categorias.length === 0 && !isLoadingCategorias) {
        return (
            <div className="alert alert-warning">
                <div className="flex items-center gap-2 mb-2">
                    <Tag size={20} />
                    <h3 className="font-medium">Categorias Necessárias</h3>
                </div>
                <p className="text-sm mb-3">
                    Para {mode === 'edit' ? 'editar' : 'criar'} serviços, você precisa primeiro criar categorias do tipo "servico".
                </p>
                <div className="flex gap-2">
                    <button onClick={onCloseForm} className="action-button action-button-secondary">Fechar</button>
                    <button onClick={() => window.location.href = '/categorias'} className="action-button action-button-primary">Criar Categorias</button>
                </div>
            </div>
        );
    }

    if (!isDataReady) {
        return (
            <div className="flex flex-col items-center justify-center p-8">
                <Loader2 size={24} className="animate-spin text-blue-500 mb-4" />
                <p className="text-gray-600">Preparando formulário...</p>
            </div>
        );
    }

    return (
        <ServicoForm
            onCloseForm={onCloseForm}
            current={mode === 'edit' ? servicoProcessado : prefillData}
            accountId={accountId}
            recursos={recursos}
            pools={pools}
            unidades={unidades}
            categorias={categorias}
            isLoadingRecursos={isLoadingRecursos}
            isErrorRecursos={isErrorRecursos}
            isLoadingPools={isLoadingPools}
            isErrorPools={isErrorPools}
            isLoadingUnidades={isLoadingUnidades}
            isErrorUnidades={isErrorUnidades}
            isLoadingCategorias={isLoadingCategorias}
            isErrorCategorias={isErrorCategorias}
            onCreateSuccess={onCreateSuccess}
            mode={mode}
        />
    );
};

export default ServicoFormWrapper;